/**
 * Pure helpers for validating internal return URLs.
 *
 * Kept free of `"use client"` so Server Components (e.g. `/cadastrar`)
 * can call them. Browser-only redirect helpers live in `./redirect`.
 *
 * Prevents CWE-601 (Open Redirect) by accepting only same-origin relative
 * paths. Rejects:
 *   - Absolute URLs (`https://evil.com`, `javascript:`, `data:`, etc.)
 *   - Protocol-relative URLs (`//evil.com`, `\\evil.com`)
 *   - Backslash-prefixed paths some browsers normalize to `//`
 *   - Empty / non-string / non-`/` prefixed values
 *
 * Anything that passes this check is guaranteed to resolve against the
 * current origin when assigned to `window.location.href` or
 * `Location.assign()`, regardless of browser URL-parsing quirks.
 */

export function isSafeReturnPath(value: unknown): value is string {
  if (typeof value !== "string" || value.length === 0) return false;
  if (value[0] !== "/") return false;
  // Block protocol-relative (`//host`) and backslash variants (`/\host`,
  // `\/host`) — browsers and URL parsers disagree on the latter, so we
  // reject any second character that isn't a "safe" path char.
  const second = value[1];
  if (second === "/" || second === "\\") return false;
  // Defense-in-depth: reject embedded control chars / whitespace which
  // some parsers strip before resolving the URL.
  if (/[\u0000-\u001f\u007f\s]/.test(value)) return false;
  return true;
}

/**
 * Returns `value` if it is a safe internal path, otherwise `fallback`.
 * Use this anywhere a user-controlled return URL is consumed.
 */
export function sanitizeReturnUrl(value: unknown, fallback = "/"): string {
  return isSafeReturnPath(value) ? value : fallback;
}

/**
 * Relative path for `authRedirect` and `router.push`.
 *
 * `URL#toString()` is absolute. `authRedirect` only accepts a same-origin
 * relative path and otherwise navigates to `/`, which drops `returnUrl`
 * (including an invite token) on the way through phone or email confirmation.
 */
export function internalAuthPath(url: URL): string {
  const path = `${url.pathname}${url.search}`;
  return isSafeReturnPath(path) ? path : "/";
}
